/**
 * **Activation-pick** — which unit a side activates next, as a thin **argmax over each candidate
 * unit's own top-ranked macro-move score** (`rankMacroMoves`'s `k: 1`). This is NOT a new
 * planning layer: it composes the SAME utility advisor that already ranks one unit's plans,
 * running it once per candidate and picking whichever unit's best plan scores highest.
 *
 * **Documented simplification:** a myopic per-unit argmax can't see that activating unit X first
 * might set up a materially better outcome for unit Y (denying a target, opening a lane, drawing
 * a reaction) — true lookahead over *activation order* is what an expectiminimax over activation
 * order would be for. This function does not attempt it; it is a 1-ply "whose best move is best"
 * comparison, nothing more.
 *
 * **Never re-derives the activation rules.** The candidate pool is `ableUnactedOnSide`
 * (`@lancer-vtt/domain`) — the EXACT pool `activate_unit`'s own legality reads (alive, un-acted
 * this round, an actor) — so a picked unit is always a legal `activate_unit` target against the
 * state it was picked from. Whether `side` is actually free to activate right now (nobody
 * mid-turn, correct `activation.activeSide`) is the CALLER's job to check before acting on the
 * pick (`autoplaySide`, `./autoplay.ts`) — this function only answers "of the units on `side`
 * that could still activate this round, which one's plan scores best," never "is it side's turn."
 */
import { ableUnactedOnSide, ACTIONS_PER_TURN, type Combatant, type CombatantId, type EncounterState, type Side, type TurnBudget } from "@lancer-vtt/domain";
import { SEED_OVERLAY, type LoadedOverlay } from "@lancer-vtt/rules";
import type { Compendium } from "@lancer-vtt/compendium";
import { rankMacroMoves } from "./advisor";
import type { FeatureTerm } from "./evaluator";
import { timePlanning } from "./perfHooks";

export type PickActivationArgs = {
  state: EncounterState;
  side: Side;
  compendium: Compendium;
  overlay?: LoadedOverlay;
  /** Forwarded to `rankMacroMoves`; defaults to `DEATHMATCH_TERMS`. */
  terms?: readonly FeatureTerm[];
  /** Forwarded to `rankMacroMoves`'s destination pruning; default 10. */
  maxDestinations?: number;
};

/**
 * `generateMacroMoves` (`generator.ts`) only proposes plans for the combatant that is ALREADY
 * `state.activation.turn.actor` — it returns `[]` for anyone else. Activation-pick needs exactly
 * the opposite: comparing candidates
 * BEFORE any of them has been activated. Mirrors `advisor.ts`'s own "position-swap-only, no
 * full adjudication" projection discipline — a pure clone that sets `turn` to what
 * `activate_unit`'s reducer case (`packages/domain/src/encounter/reducer.ts`'s `activate`)
 * would produce for `c` (full action budget via the exported `ACTIONS_PER_TURN`, move budget
 * `c.base.speed`), composing the same exported constant the reducer itself uses rather than
 * inventing a number. This is a RANKING-time estimate only — `autoplaySide` (`./autoplay.ts`)
 * still submits a real `activate_unit` command and lets the authority construct the actual
 * budget; nothing here is submitted to the wire.
 *
 * **Exported for `./search.ts`** — the search's opponent-reply ply needs to score a
 * not-yet-activated candidate exactly this way; it reuses this ONE projection rather than
 * keeping a second copy.
 */
export function withHypotheticalActivation(state: EncounterState, c: Combatant): EncounterState {
  const budget: TurnBudget = { actions: ACTIONS_PER_TURN, move: c.base.speed, actionUses: {}, protocolUses: {} };
  return { ...state, activation: { ...state.activation, turn: { phase: "acting", actor: c.id, budget } } };
}

/**
 * The still-activatable unit on `side` whose best available plan
 * (`rankMacroMoves({ actorId, k: 1 })`'s top score, evaluated as if THAT unit were the one
 * activating — see {@link withHypotheticalActivation}) is highest, or `null` when `side` has
 * no able un-acted units left this round (`ableUnactedOnSide` is empty — the caller's cue to
 * stop driving this side, not an error).
 */
export function pickActivation(args: PickActivationArgs): CombatantId | null {
  const { state, side, compendium, overlay = SEED_OVERLAY, terms, maxDestinations } = args;
  const candidates = ableUnactedOnSide(state, side);
  if (candidates.length === 0) return null;

  let bestId: CombatantId | null = null;
  let bestScore = -Infinity;
  for (const c of candidates) {
    // `timePlanning` (`./perfHooks.ts`) is a no-op wrapper unless explicitly enabled — a single
    // boolean check when disabled. This IS the same advisor call `autoplayActivation` times
    // (`./autoplay.ts`), from a different call site (per-candidate ranking, not the acting unit's
    // own re-planning), so both land in the same "planning" bucket.
    const ranked = timePlanning(() =>
      rankMacroMoves({
        state: withHypotheticalActivation(state, c),
        actorId: c.id,
        compendium,
        overlay,
        k: 1,
        ...(terms ? { terms } : {}),
        ...(maxDestinations !== undefined ? { maxDestinations } : {}),
      }),
    );
    const score = ranked[0]?.score ?? -Infinity;
    if (bestId === null || score > bestScore) {
      bestId = c.id;
      bestScore = score;
    }
  }
  return bestId;
}
